import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filter, Loader2, Megaphone, Search, X } from 'lucide-react';
import { useCampaignStatusQuery, useMyCampaignsQuery } from '@/features/campaigns/api/useCampaignQueries';
import { CampaignListCard } from '@/features/campaigns/components/CampaignListCard';
import { CampaignModeSelectModal } from '@/features/campaigns/components/CampaignModeSelectModal';
import { ClientPersonalAdvertiserPrompt } from '@/features/campaigns/components/ClientPersonalAdvertiserPrompt';
import { CAMPAIGN_STATUS_OPTIONS, STATUS_COLORS } from '@/features/campaigns/utils/campaignListUtils';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { useIsClient } from '@/shared/hooks/useIsClient';
import { useAppSelector } from '@/shared/store/hooks';

const PAGE_SIZE_OPTIONS = [15, 30, 60, 100] as const;

type CampaignsPageProps = {
  openCreateModal?: boolean;
  onCreateModalConsumed?: () => void;
};

export function CampaignsPage({
  openCreateModal = false,
  onCreateModalConsumed,
}: CampaignsPageProps) {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('All');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] =
    useState<(typeof PAGE_SIZE_OPTIONS)[number]>(15);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const navigate = useNavigate();
  const isClient = useIsClient();
  const user = useAppSelector((s) => s.auth.user);
  const clientId = String(user?.id || user?._id || '').trim();
  const debouncedSearch = useDebounce(search, 400);

  useEffect(() => {
    if (!openCreateModal) return;
    setCreateOpen(true);
    onCreateModalConsumed?.();
  }, [openCreateModal, onCreateModalConsumed]);

  const filters = useMemo(
    () => ({
      page,
      limit: pageSize,
      search: debouncedSearch,
      status,
      ...(startDate ? { startDate } : {}),
      ...(endDate && (!startDate || endDate >= startDate) ? { endDate } : {}),
      ...(isClient && clientId ? { agencyClientId: String(clientId) } : {}),
    }),
    [page, pageSize, debouncedSearch, status, startDate, endDate, isClient, clientId],
  );

  const query = useMyCampaignsQuery(filters, {
    enabled: !isClient || Boolean(clientId),
  });
  const campaigns = query.data?.data?.campaigns ?? [];
  const pagination = query.data?.data?.pagination;
  const totalPages = pagination?.totalPages || 1;
  const totalCount =
    pagination?.totalCount ?? pagination?.total ?? campaigns.length;
  const dateRangeInvalid = Boolean(startDate && endDate && endDate < startDate);
  const hasFilters =
    Boolean(debouncedSearch) ||
    status !== 'All' ||
    Boolean(startDate) ||
    Boolean(endDate);

  const isUpdating = query.isFetching && !query.isPending;
  const statusCountQuery = useCampaignStatusQuery();
  const rawStatusData = statusCountQuery.data?.data || statusCountQuery.data || {};
  const statusCountsMap = (rawStatusData?.statusCounts || {}) as Record<
    string,
    { count?: number; totalAmount?: number }
  >;
  // const totalStatusCount =
  //   rawStatusData?.summary?.totalCampaigns ??
  //   Object.values(statusCountsMap).reduce(
  //     (acc, curr) => acc + (curr?.count || 0),
  //     0,
  //   );

  const clearAllFilters = () => {
    setSearch('');
    setStatus('All');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, totalCount);

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-5">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="text-left">
          <h1 className="m-0 mt-1 text-2xl font-semibold tracking-tight text-[var(--dash-ink)]">
            Your campaigns
          </h1>
          <p className="mt-1 mb-0 text-sm text-[var(--dash-muted)]">
            {query.isPending || isUpdating
              ? 'Loading your orders…'
              : isClient
                ? `${totalCount} campaign${totalCount === 1 ? '' : 's'} · view and track`
                : `${totalCount} campaign${totalCount === 1 ? '' : 's'} · manage, track, and launch campaigns`}
          </p>
        </div>

        <button
          type="button"
          onClick={() => setFiltersOpen((v) => !v)}
          className={`inline-flex items-center gap-1.5 rounded-xl border px-3.5 py-2.5 text-sm font-semibold transition ${filtersOpen || hasFilters
            ? 'border-[var(--dash-brand)] bg-[var(--dash-brand-soft)] text-[var(--dash-brand)]'
            : 'border-[var(--dash-line)] bg-white text-[var(--dash-ink)]'
            }`}
        >
          <Filter className="h-4 w-4 text-[#8156B4]" />
          Filter
          {hasFilters ? (
            <span className="ml-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#E5384F] px-1.5 text-[10px] font-extrabold text-white">
              !
            </span>
          ) : null}
        </button>
      </header>

      {isClient ? <ClientPersonalAdvertiserPrompt variant="banner" /> : null}

      {filtersOpen ? (
        <div className="camp-fade-up flex flex-col gap-3.5 rounded-2xl border border-[var(--dash-line)] bg-white p-3.5 shadow-[0_10px_28px_-22px_rgba(16,38,73,0.35)] sm:p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-[var(--dash-muted)]" />
              <input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Search Order ID, Name"
                className="w-full border-none bg-transparent pr-7 pl-8 text-[14px] font-medium text-[#3A3660] placeholder-[#6A6688] outline-none"
              />
              {search ? (
                <button
                  type="button"
                  onClick={() => {
                    setSearch('');
                    setPage(1);
                  }}
                  className="absolute right-3 flex h-5 w-5 items-center justify-center rounded-full bg-white text-[#6A6688] shadow-sm transition hover:text-[#3A3660]"
                  aria-label="Clear search"
                >
                  <X className="h-3 w-3" strokeWidth={2.5} />
                </button>
              ) : null}
            </div>

            <div className="grid min-w-0 w-full grid-cols-1 gap-2.5 sm:grid-cols-2 sm:gap-3 lg:w-[24rem] lg:shrink-0">
              <label className="flex min-w-0 w-full max-w-full flex-col gap-1.5 text-left">
                <span className="text-[11px] font-semibold tracking-[0.06em] text-[var(--dash-muted)] uppercase">
                  Start date
                </span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    const value = e.target.value;
                    setStartDate(value);
                    setEndDate((prev) =>
                      prev && value && prev < value ? '' : prev,
                    );
                    setPage(1);
                  }}
                  className="camp-date-input h-11 min-w-0 w-full max-w-full rounded-xl border border-[var(--dash-line)] bg-[var(--dash-soft)] px-3 text-sm text-[var(--dash-ink)] outline-none transition focus:border-[var(--dash-brand)] focus:bg-white focus:ring-2 focus:ring-[var(--dash-brand)]/15"
                />
              </label>
              <label
                className={`flex min-w-0 w-full max-w-full flex-col gap-1.5 text-left ${!startDate ? 'opacity-60' : ''
                  }`}
              >
                <span className="text-[11px] font-semibold tracking-[0.06em] text-[var(--dash-muted)] uppercase">
                  End date
                </span>
                <input
                  type="date"
                  value={endDate}
                  min={startDate || undefined}
                  disabled={!startDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setPage(1);
                  }}
                  className={`camp-date-input h-11 min-w-0 w-full max-w-full rounded-xl border bg-[var(--dash-soft)] px-3 text-sm text-[var(--dash-ink)] outline-none transition focus:border-[var(--dash-brand)] focus:bg-white focus:ring-2 focus:ring-[var(--dash-brand)]/15 disabled:cursor-not-allowed ${dateRangeInvalid
                    ? 'border-red-400'
                    : 'border-[var(--dash-line)]'
                    }`}
                />
              </label>
            </div>
          </div>

          {dateRangeInvalid ? (
            <p className="m-0 text-left text-xs font-semibold text-[#E5384F]">
              End date must be on or after the start date.
            </p>
          ) : null}

          <div className="flex flex-col gap-2.5 border-t border-[var(--dash-line)]/70 pt-3 sm:flex-row sm:items-center">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setStatus('All');
                  setPage(1);
                }}
                className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all ${STATUS_COLORS.All.bg
                  } ${STATUS_COLORS.All.text} ${status === 'All'
                    ? 'border border-neutral-300 shadow-sm ring-2 ring-neutral-400/40'
                    : 'border border-transparent hover:opacity-90'
                  }`}
              >
                <span className={`h-2 w-2 rounded-full ${STATUS_COLORS.All.dotBg}`} />
                <span>All</span>
                {/* <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-bold text-neutral-800 shadow-2xs">
                  {totalStatusCount}
                </span> */}
              </button>

              {Object.entries(statusCountsMap)
                .filter(([key, value]) => (value?.count ?? 0) > 0 || status === key)
                .map(([key, value]) => {
                  const active = status === key;
                  const count = value?.count ?? 0;
                  const match = CAMPAIGN_STATUS_OPTIONS.find((o) => o.value === key);
                  const label = match ? match.label : key.replaceAll('_', ' ');
                  const style = STATUS_COLORS[key] || STATUS_COLORS.DRAFT;

                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        setStatus(key);
                        setPage(1);
                      }}
                      className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all ${style.bg
                        } ${style.text} ${active
                          ? 'border border-current/40 shadow-sm ring-2 ring-offset-1 ring-current/60'
                          : 'border border-transparent hover:opacity-90'
                        }`}
                    >
                      <span className={`h-2 w-2 rounded-full ${style.dotBg}`} />
                      <span>{label}</span>
                      <span className="rounded-full bg-white/90 px-2 py-0.5 text-[11px] font-bold shadow-2xs">
                        {count}
                      </span>
                    </button>
                  );
                })}
            </div>

            {hasFilters ? (
              <button
                type="button"
                onClick={clearAllFilters}
                className="shrink-0 self-start text-[13px] font-semibold text-[#B4233A] hover:underline sm:self-auto"
              >
                Clear all
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {query.isPending ? (
        <div className="flex min-h-[360px] flex-col items-center justify-center rounded-3xl border border-[var(--dash-line)] bg-white py-16">
          <Loader2 className="h-9 w-9 animate-spin text-[var(--dash-brand)]" />
          <p className="mt-3 text-xs font-semibold text-[var(--dash-muted)]">
            Loading campaigns…
          </p>
        </div>
      ) : campaigns.length === 0 ? (
        <div className="flex min-h-[42vh] flex-col items-center justify-center rounded-[24px] bg-white px-6 py-12 text-center shadow-[0_10px_30px_rgba(60,40,120,0.05)]">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#DFE9FA] text-[#3B66B8]">
            <Megaphone className="h-7 w-7" />
          </div>
          <h2 className="m-0 text-lg font-bold text-[#3A3660]">
            {hasFilters ? 'No matching campaigns' : 'No campaigns yet'}
          </h2>
          <p className="mt-2 mb-6 max-w-sm text-sm text-[#6A6688]">
            {hasFilters
              ? 'Try clearing filters or searching a different name.'
              : isClient
                ? 'Campaigns created for you by your agency will appear here.'
                : 'Launch your first billboard campaign in a few guided steps.'}
          </p>
          {hasFilters ? (
            <button
              type="button"
              onClick={clearAllFilters}
              className="dash-btn-gradient inline-flex h-[42px] items-center gap-1.5 rounded-full px-5 text-[14px] font-bold text-white"
            >
              Clear filters
            </button>
          ) : isClient ? (
            <ClientPersonalAdvertiserPrompt variant="card" />
          ) : (
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="dash-btn-gradient inline-flex h-[42px] items-center gap-1.5 rounded-full px-5 text-[14px] font-bold text-white shadow-[0_8px_20px_-8px_rgba(59,102,184,0.5)]"
            >
              + New Campaign
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="relative min-h-[300px]">
            {isUpdating ? (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-xl border border-[var(--dash-line)]">
                  <Loader2 className="h-7 w-7 animate-spin text-[var(--dash-brand)]" />
                </div>
              </div>
            ) : null}

            <div
              className={`grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 transition-opacity duration-200 ${isUpdating ? 'opacity-40 pointer-events-none' : 'opacity-100'
                }`}
            >
              {campaigns.map((campaign) => (
                <CampaignListCard
                  key={campaign.campaignId || campaign._id}
                  campaign={campaign}
                  onView={(cid) => navigate(`/campaigns/${cid}`)}
                  onRelaunch={
                    isClient
                      ? undefined
                      : (cid) => navigate(`/campaigns/${cid}/relaunch`)
                  }
                />
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-3 rounded-[20px] bg-white px-5 py-3.5 shadow-[0_10px_30px_rgba(60,40,120,0.05)] sm:flex-row sm:items-center sm:justify-between">
            <p className="m-0 text-left text-sm text-[#6A6688]">
              Showing{' '}
              <span className="font-bold text-[#3A3660]">
                {rangeStart}–{rangeEnd}
              </span>{' '}
              of{' '}
              <span className="font-bold text-[#3A3660]">
                {totalCount}
              </span>
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex items-center gap-2 text-sm text-[#6A6688]">
                Per page
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(
                      Number(
                        e.target.value,
                      ) as (typeof PAGE_SIZE_OPTIONS)[number],
                    );
                    setPage(1);
                  }}
                  className="rounded-[10px] border border-[#EAE8F5] bg-[#F3F2F9] px-2.5 py-1.5 text-sm font-bold text-[#3A3660] outline-none focus:border-[#3B66B8]"
                  aria-label="Campaigns per page"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>

              {totalPages > 1 ? (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="rounded-[12px] border border-[#EAE8F5] bg-white px-3.5 py-1.5 text-sm font-bold text-[#3A3660] transition hover:bg-[#F3F2F9] disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <span className="min-w-[4.5rem] text-center text-sm font-semibold text-[#6A6688]">
                    {page} / {totalPages}
                  </span>
                  <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    className="rounded-[12px] border border-[#EAE8F5] bg-white px-3.5 py-1.5 text-sm font-bold text-[#3A3660] transition hover:bg-[#F3F2F9] disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </>
      )}

      <CampaignModeSelectModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </div>
  );
}
